import type { Api } from "@rhema/client";
import { setPrefs } from "./prefs";

// Web push. The server sends a wake-up with the chat and the sender's name, never the message.
// The service worker in public/sw.js shows it.

/** The browser features push needs, so tests can stand in for them. */
export type PushEnv = {
  permission: () => NotificationPermission | "unsupported";
  requestPermission: () => Promise<NotificationPermission>;
  registration: () => Promise<ServiceWorkerRegistration | null>;
};

export function browserPushEnv(): PushEnv {
  const supported =
    typeof window !== "undefined" &&
    "Notification" in window &&
    "serviceWorker" in navigator &&
    "PushManager" in window;
  return {
    permission: () => (supported ? Notification.permission : "unsupported"),
    requestPermission: () => Notification.requestPermission(),
    registration: async () => {
      if (!supported) return null;
      await navigator.serviceWorker.register("/sw.js", { scope: "/" });
      return navigator.serviceWorker.ready;
    },
  };
}

export type PushResult = "on" | "denied" | "unsupported" | "unavailable";

/** The VAPID key arrives as unpadded base64url. PushManager wants the raw bytes. */
export function vapidKeyBytes(key: string): Uint8Array<ArrayBuffer> {
  const padded = key.replace(/-/g, "+").replace(/_/g, "/") + "=".repeat((4 - (key.length % 4)) % 4);
  const raw = atob(padded);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

/** Asks for permission, subscribes this browser and tells the server. Call it from a tap or click. */
export async function enablePush(api: Api, env: PushEnv = browserPushEnv()): Promise<PushResult> {
  if (env.permission() === "unsupported") return "unsupported";
  const permission = env.permission() === "granted" ? "granted" : await env.requestPermission();
  if (permission !== "granted") {
    setPrefs({ enabled: false });
    return "denied";
  }
  // From here on in-app notifications work even if push itself cannot be set up.
  setPrefs({ enabled: true });
  const { vapid_public_key: key } = await api.pushConfig();
  const registration = await env.registration();
  if (!key || !registration) return "unavailable";
  const subscription =
    (await registration.pushManager.getSubscription()) ??
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: vapidKeyBytes(key),
    }));
  const json = subscription.toJSON();
  const p256dh = json.keys?.p256dh;
  const auth = json.keys?.auth;
  if (!p256dh || !auth) return "unavailable";
  await api.subscribePush({ kind: "webpush", token: subscription.endpoint, keys: { p256dh, auth } });
  return "on";
}

/** Stops push for this browser and removes it from the server. */
export async function disablePush(api: Api, env: PushEnv = browserPushEnv()): Promise<void> {
  setPrefs({ enabled: false });
  if (env.permission() !== "unsupported") {
    const registration = await env.registration().catch(() => null);
    const subscription = await registration?.pushManager.getSubscription();
    await subscription?.unsubscribe().catch(() => false);
  }
  await api.unsubscribePush();
}
